import {
  KEY_TARGETS,
  TARGET_HIT_RADIUS_REM,
  TARGET_RADIUS_REM,
} from '../../shared/config';
import { remToPx } from '../../shared/scale';
import type { InputEvent, Target, TargetMatch, TargetState } from '../target';

const COLOR_KEYS = ['pink', 'yellow', 'teal', 'purple'] as const;
type ColorKey = (typeof COLOR_KEYS)[number];

const KEY_COLOR: Readonly<Record<string, ColorKey>> = Object.freeze({
  w: 'pink',
  a: 'yellow',
  s: 'teal',
  d: 'purple',
});

const SVG_NS = 'http://www.w3.org/2000/svg';
const RING_RADIUS = 74;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

export interface BoopTargetOptions {
  id: number;
  x: number;
  y: number;
  lifetimeMs: number;
  initialColorIndex: number;
  clock: () => number;
}

export function createBoopTarget(options: BoopTargetOptions): Target {
  const { id, x, y, lifetimeMs, initialColorIndex, clock } = options;

  let state: TargetState = 'circle';
  let key: string | null = null;
  let colorKey: ColorKey = COLOR_KEYS[initialColorIndex % COLOR_KEYS.length] ?? 'teal';
  let spawnedAt = clock();
  let root: HTMLDivElement | null = null;
  let body: HTMLDivElement | null = null;
  let ringProgress: SVGCircleElement | null = null;

  function buildRing(): SVGSVGElement {
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('class', 'target-ring');
    svg.setAttribute('viewBox', '0 0 160 160');

    const bg = document.createElementNS(SVG_NS, 'circle');
    bg.setAttribute('class', 'target-ring-bg');
    bg.setAttribute('cx', '80');
    bg.setAttribute('cy', '80');
    bg.setAttribute('r', String(RING_RADIUS));
    svg.appendChild(bg);

    const fg = document.createElementNS(SVG_NS, 'circle');
    fg.setAttribute('class', 'target-ring-progress');
    fg.setAttribute('cx', '80');
    fg.setAttribute('cy', '80');
    fg.setAttribute('r', String(RING_RADIUS));
    fg.setAttribute('stroke-dasharray', String(RING_CIRCUMFERENCE));
    fg.setAttribute('stroke-dashoffset', '0');
    svg.appendChild(fg);

    ringProgress = fg;
    return svg;
  }

  function buildCircleBody(): HTMLDivElement {
    const wrap = document.createElement('div');
    wrap.className = 'target-circle';

    const highlight = document.createElement('div');
    highlight.className = 'target-circle-highlight';
    wrap.appendChild(highlight);

    const center = document.createElement('div');
    center.className = 'target-circle-center';
    wrap.appendChild(center);

    return wrap;
  }

  function buildLetterBody(letter: string): HTMLDivElement {
    const card = document.createElement('div');
    card.className = 'target-letter';

    const arrow = document.createElement('div');
    arrow.className = 'target-letter-arrow';
    arrow.textContent = '⌨️';
    card.appendChild(arrow);

    const glyph = document.createElement('span');
    glyph.className = 'target-letter-glyph';
    glyph.textContent = letter.toUpperCase();
    card.appendChild(glyph);

    return card;
  }

  function syncDataset(): void {
    if (!root) return;
    root.dataset.state = state;
    root.dataset.color = colorKey;
    if (key !== null) root.dataset.key = key;
    else delete root.dataset.key;
  }

  function transformToLetter(): void {
    const pick = KEY_TARGETS[Math.floor(Math.random() * KEY_TARGETS.length)] ?? 'w';
    key = pick;
    colorKey = KEY_COLOR[pick] ?? 'teal';
    state = 'letter';
    spawnedAt = clock();

    if (root && body) {
      const next = buildLetterBody(pick);
      root.replaceChild(next, body);
      body = next;
      syncDataset();
    }
  }

  function render(parent: HTMLElement): void {
    root = document.createElement('div');
    root.className = 'target';
    root.style.setProperty('--target-x', `${x}px`);
    root.style.setProperty('--target-y', `${y}px`);
    root.style.setProperty('--target-radius', `${remToPx(TARGET_RADIUS_REM)}px`);
    root.style.setProperty('--ring-circumference', String(RING_CIRCUMFERENCE));
    root.style.setProperty('--ring-offset', '0');

    const ring = buildRing();
    root.appendChild(ring);

    body = buildCircleBody();
    root.appendChild(body);

    syncDataset();
    parent.appendChild(root);
  }

  function matches(event: InputEvent): TargetMatch {
    if (state === 'circle' && event.type === 'click') {
      const dx = event.x - x;
      const dy = event.y - y;
      const hitRadius = remToPx(TARGET_HIT_RADIUS_REM);
      if (dx * dx + dy * dy <= hitRadius * hitRadius) {
        transformToLetter();
        return 'progress';
      }
      return 'none';
    }
    if (state === 'letter' && event.type === 'key' && key !== null && event.key === key) {
      return 'complete';
    }
    return 'none';
  }

  function tick(now: number): void {
    if (!root || !ringProgress) return;
    const age = now - spawnedAt;
    const remaining = Math.max(0, 1 - age / lifetimeMs);
    const offset = RING_CIRCUMFERENCE * (1 - remaining);
    ringProgress.setAttribute('stroke-dashoffset', String(offset));
  }

  function isExpired(now: number): boolean {
    return now - spawnedAt > lifetimeMs;
  }

  function destroy(): void {
    root?.remove();
    root = null;
    body = null;
    ringProgress = null;
  }

  return {
    id,
    x,
    y,
    get state() {
      return state;
    },
    get key() {
      return key;
    },
    get colorKey() {
      return colorKey;
    },
    render,
    matches,
    tick,
    isExpired,
    destroy,
  };
}
